import { useState, useEffect, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

export default function ImageLightbox({
  open,
  url,
  handleClose,
  download,
  imageName = 'image',
  onPrev,
  onNext,
  mediaType = 'image'
}) {
  const { t } = useTranslation();
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const resetView = () => {
    setRotation(0);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
    setImageLoaded(false);
  };

  // Reset on close or when navigating to another media
  useEffect(() => {
    resetView();
  }, [open, url, mediaType]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  // ESC / arrow key handlers
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
        return;
      }
      if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handleClose, onPrev, onNext]);

  // Wheel zoom
  useEffect(() => {
    if (!open || !containerRef.current) return;

    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setScale((prev) => {
          const newScale = Math.max(0.5, Math.min(5, prev + delta));
          if (newScale <= 1) {
            setPosition({ x: 0, y: 0 });
          }
          return newScale;
        });
      }
    };

    const container = containerRef.current;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [open]);

  const handleRotateLeft = () => {
    setRotation((prev) => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation((prev) => prev + 90);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(5, prev + 0.25));
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(0.5, prev - 0.25);
      if (newScale <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1 && imageRef.current) {
      e.preventDefault();
      const rect = imageRef.current.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();

      let newX = e.clientX - dragStart.x;
      let newY = e.clientY - dragStart.y;

      if (containerRect) {
        const maxX = (rect.width * scale - containerRect.width) / 2;
        const maxY = (rect.height * scale - containerRect.height) / 2;
        newX = Math.max(-maxX, Math.min(maxX, newX));
        newY = Math.max(-maxY, Math.min(maxY, newY));
      }

      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (scale > 1 && e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && scale > 1 && e.touches.length === 1 && imageRef.current) {
      e.preventDefault();
      const touch = e.touches[0];
      const rect = imageRef.current.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();

      let newX = touch.clientX - dragStart.x;
      let newY = touch.clientY - dragStart.y;

      if (containerRect) {
        const maxX = (rect.width * scale - containerRect.width) / 2;
        const maxY = (rect.height * scale - containerRect.height) / 2;
        newX = Math.max(-maxX, Math.min(maxX, newX));
        newY = Math.max(-maxY, Math.min(maxY, newY));
      }

      setPosition({ x: newX, y: newY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleDownload = () => {
    if (download) {
      download();
    } else if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = imageName || 'image';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleDoubleClick = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const navButtonSx = {
    position: 'fixed',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 10000,
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.28)'
    },
    width: { xs: 44, sm: 52 },
    height: { xs: 44, sm: 52 }
  };

  if (!open || !url) return null;

  return (
    <Box
      ref={containerRef}
      onClick={handleBackdropClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: scale > 1 && isDragging ? 'grabbing' : scale > 1 ? 'grab' : 'default',
        overflow: 'hidden',
        touchAction: 'none',
        WebkitOverflowScrolling: 'touch'
      }}>
      <Box
        ref={imageRef}
        component={mediaType === 'video' ? 'video' : 'img'}
        src={url}
        alt={mediaType === 'video' ? undefined : 'Preview'}
        muted={mediaType === 'video' ? true : undefined}
        playsInline={mediaType === 'video' ? true : undefined}
        autoPlay={mediaType === 'video' ? true : undefined}
        loop={mediaType === 'video' ? true : undefined}
        controls={mediaType === 'video' ? false : undefined}
        onLoad={mediaType === 'video' ? undefined : handleImageLoad}
        onLoadedData={mediaType === 'video' ? handleImageLoad : undefined}
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={handleDoubleClick}
        onTouchStart={(e) => e.stopPropagation()}
        sx={{
          maxWidth: '100vw',
          maxHeight: 'calc(100vh - 100px)',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          transform: `rotate(${rotation}deg) scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
          cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          touchAction: 'none',
          opacity: imageLoaded ? 1 : 0,
          transition: isDragging ? 'none' : imageLoaded ? 'opacity 0.2s' : 'none'
        }}
      />

      {onPrev && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          sx={{ ...navButtonSx, left: { xs: 8, sm: 16 } }}
          title={t('Previous')}>
          <ChevronLeftIcon fontSize="large" />
        </IconButton>
      )}

      {onNext && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          sx={{ ...navButtonSx, right: { xs: 8, sm: 16 } }}
          title={t('Next')}>
          <ChevronRightIcon fontSize="large" />
        </IconButton>
      )}

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1, sm: 2 },
          py: 2,
          px: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
        <IconButton
          onClick={handleRotateLeft}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            },
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 }
          }}
          title={t('Chapga aylantirish')}>
          <RotateLeftIcon />
        </IconButton>

        <IconButton
          onClick={handleRotateRight}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            },
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 }
          }}
          title={t("O'ngga aylantirish")}>
          <RotateRightIcon />
        </IconButton>

        <IconButton
          onClick={handleZoomOut}
          disabled={scale <= 0.5}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            },
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.3)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            },
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 }
          }}
          title={t('Kichiklashtirish')}>
          <ZoomOutIcon />
        </IconButton>

        <IconButton
          onClick={handleZoomIn}
          disabled={scale >= 5}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            },
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.3)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            },
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 }
          }}
          title={t('Kattalashtirish')}>
          <ZoomInIcon />
        </IconButton>

        <IconButton
          onClick={handleDownload}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            },
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 }
          }}
          title={t('Yuklab olish')}>
          <DownloadIcon />
        </IconButton>

        <IconButton
          onClick={handleClose}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 0, 0, 0.3)'
            },
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 }
          }}
          title={t('Yopish')}>
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
